import React, { useState } from 'react';
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon, Description as CsvIcon, FileDownloadOutlined as DownloadIcon, TableChart as ExcelIcon } from '@mui/icons-material';
import { getErrorMessage } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { saveBlob } from '../../utils/download';
import BusyButton from '../form/BusyButton';

/**
 * The record types a spreadsheet can hold, in the order the workbook's sheets
 * come. Matches the API's export tables and template sheets.
 */
export const RECORD_TYPES = [
  { key: 'surveys', label: 'Surveys' },
  { key: 'transects', label: 'Transects' },
  { key: 'quadrats', label: 'Quadrats' },
  { key: 'trees', label: 'Mature trees' },
  { key: 'regeneration', label: 'Regeneration counts' },
  { key: 'disturbances', label: 'Disturbances' },
];

/**
 * A button that opens a menu of files to download: the Excel workbook (every
 * record type, one sheet each), then a CSV for each record type. Export and
 * Download a template both use it.
 *
 * `fetchFile(format, typeKey)` returns `{ blob, filename }`. `excelHint` is the
 * workbook's second line, `note` an optional line at the top of the menu, and
 * `failure` the toast when a download fails ("Could not download …").
 */
const DownloadMenu = ({ id, label, types = RECORD_TYPES, fetchFile, excelHint, note, failure, disabled = false, buttonProps }) => {
  const toast = useToast();
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = async (format, typeKey) => {
    setAnchor(null);
    setBusy(true);
    try {
      const { blob, filename } = await fetchFile(format, typeKey);
      saveBlob(blob, filename);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(getErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BusyButton
        busy={busy}
        startIcon={<DownloadIcon />}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? id : undefined}
        {...buttonProps}
      >
        {label}
      </BusyButton>
      <Menu id={id} anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {note}
        <MenuItem onClick={() => download('xlsx')}>
          <ListItemIcon><ExcelIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Excel workbook (.xlsx)" secondary={excelHint} />
        </MenuItem>
        <Divider />
        <ListSubheader>CSV – one record type</ListSubheader>
        {types.map((t) => (
          <MenuItem key={t.key} onClick={() => download('csv', t.key)}>
            <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default DownloadMenu;
